import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { ChevronDown, Eye, Trash2, Upload, FileText, FileImage, File as FileIcon, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import api, { apiError } from "../lib/api";
import { formatDate } from "../lib/utils-format";
import { DOC_CATEGORIES } from "../config/homebuilding";

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fileIcon = (ct = "") => (ct.startsWith("image/") ? FileImage : ct === "application/pdf" ? FileText : FileIcon);

export function RecordDocuments({ recordId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("Photos");
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/documents?record_id=${recordId}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [recordId]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", recordId);
      fd.append("category", category);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const preview = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: doc.content_type || res.data.type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/documents/${toDelete.id}`); toast.success("Document removed"); setToDelete(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="record-documents-card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-6 py-3.5 border-b border-border bg-secondary/30 flex items-center justify-between gap-3 text-left hover:bg-secondary/50 transition-colors" data-testid="record-documents-toggle">
        <div className="text-sm font-semibold">Documents{items.length ? ` (${items.length})` : ""}</div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div>
          <div className="px-6 py-3 border-b border-border flex flex-wrap items-center gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px] h-9" data-testid="record-doc-category"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="record-doc-file" />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="record-doc-upload-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {busy ? "Uploading…" : "Attach File"}
            </Button>
            <span className="text-xs text-muted-foreground">PDF, images, docs up to 50 MB</span>
          </div>
          {loading ? (
            <div className="px-6 py-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-6 py-6 text-sm text-muted-foreground" data-testid="record-doc-empty">No documents attached yet — attach plans, permits, photos or contracts.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {items.map((d) => {
                  const Icon = fileIcon(d.content_type);
                  return (
                    <tr key={d.id} className="border-b border-border/50 last:border-0" data-testid={`record-doc-row-${d.id}`}>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-primary shrink-0" />
                          <span className="font-medium truncate max-w-[260px]">{d.original_filename}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap hidden sm:table-cell">{d.category}</td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap hidden md:table-cell">{fmtSize(d.size)}</td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap hidden lg:table-cell">{formatDate(d.created_at)}</td>
                      <td className="px-6 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => preview(d)} className="p-1.5 rounded hover:bg-accent" title="Open in new window" data-testid={`record-doc-preview-${d.id}`}><Eye className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setToDelete(d)} className="p-1.5 rounded hover:bg-accent text-destructive" data-testid={`record-doc-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove document?</AlertDialogTitle>
            <AlertDialogDescription>This removes “{toDelete?.original_filename}” from this record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="record-doc-confirm-delete">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
